import { FC, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ReactComponent as HaptticLogo } from "src/assets/svg/Hapttic's Logo.svg"
import { useDisableScroll } from 'src/hooks/useDisableScroll'
import classes from './analysisInProgressModal.module.scss'

interface AnalysisInProgressModalProps {
  isOpen: boolean
  onClose: () => void
}

export const AnalysisInProgressModal: FC<AnalysisInProgressModalProps> = ({
  isOpen,
  onClose,
}) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  useDisableScroll(isOpen)

  if (!isOpen) return null

  return createPortal(
    <div className={classes.modalOverlay}>
      <div className={classes.modalContent} ref={ref}>
        <div className={classes.modalHeader}>
          <h2 className={classes.modalTitle}>Analysis in Progress</h2>
          <button
            className={classes.closeButton}
            onClick={onClose}
            type="button"
          >
            <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </button>
        </div>

        <div className={classes.modalBody}>
          <div className={classes.progressContainer}>
            <div className={classes.progressRing}>
              <svg className={classes.progressSvg} viewBox="0 0 64 64">
                <circle
                  className={classes.progressCircleBack}
                  cx="32"
                  cy="32"
                  r="28"
                />
                <circle
                  className={classes.progressCircle}
                  cx="32"
                  cy="32"
                  r="28"
                />
              </svg>
              <HaptticLogo className={classes.logoIcon} />
            </div>
          </div>

          <p className={classes.message}>
            We&apos;re analyzing the content across platforms. This may take up
            to ~1 hour.
          </p>

          <p className={classes.subMessage}>
            Feel free to leave this page - we&apos;ll save your results.
          </p>
        </div>

        <div className={classes.modalFooter}>
          <button
            className={classes.doneButton}
            onClick={onClose}
            type="button"
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
